// ModernSearchBar.js - FIXED to use allTickers from your API
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const TRENDING_TICKERS = [
  { ticker: 'PETR4', name: 'Petrobras PN', category: '🔥 Trending', type: 'stock' },
  { ticker: 'VALE3', name: 'Vale ON', category: '🔥 Trending', type: 'stock' },
  { ticker: 'ITUB4', name: 'Itaú Unibanco PN', category: '🔥 Trending', type: 'stock' },
  { ticker: 'BBDC4', name: 'Bradesco PN', category: '⭐ Popular', type: 'stock' },
  { ticker: 'ABEV3', name: 'Ambev ON', category: '⭐ Popular', type: 'stock' },
  { ticker: 'MGLU3', name: 'Magazine Luiza ON', category: '⭐ Popular', type: 'stock' },
  { ticker: 'B3SA3', name: 'B3 ON', category: '⭐ Popular', type: 'stock' },
  { ticker: '^BVSP', name: 'Ibovespa', category: '📊 Índices', type: 'index' },
  { ticker: 'USDBRL=X', name: 'Dólar', category: '💱 Moedas', type: 'currency' },
  { ticker: 'BTC-USD', name: 'Bitcoin', category: '₿ Crypto', type: 'crypto' },
];

export const ModernSearchBar = ({ theme, isDark, marketData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [allAssets, setAllAssets] = useState([]);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // ============================================
  // BUILD INDEX FROM MARKETDATA.ALLTICKERS
  // ============================================
  useEffect(() => {
    if (!marketData) {
      console.warn('⚠️ marketData is null! Using trending tickers only.');
      setAllAssets(TRENDING_TICKERS);
      return;
    }

    console.log('📊 Building search index from marketData');

    const assets = [];
    const seen = new Set();

    // ADD ALL TICKERS (This is the main array with 1185+ stocks!)
    if (marketData.allTickers && Array.isArray(marketData.allTickers)) {
      console.log(`✅ Found allTickers array with ${marketData.allTickers.length} items`);
      
      marketData.allTickers.forEach(stock => {
        if (!seen.has(stock.ticker)) {
          assets.push({
            ticker: stock.ticker,
            name: stock.name || stock.ticker,
            category: '📈 Ações',
            type: 'stock',
            price: stock.ult,
            change: stock.var
          });
          seen.add(stock.ticker);
        }
      });
      console.log(`✅ Indexed ${assets.length} stocks from allTickers`);
    } else {
      console.warn('⚠️ marketData.allTickers not found or not an array!');
    }

    // Add top gainers with special badge (these already exist in allTickers but we badge them)
    if (marketData.topGainers && Array.isArray(marketData.topGainers)) {
      marketData.topGainers.forEach(gainer => {
        // Find existing entry and update category
        const existing = assets.find(a => a.ticker === gainer.ticker);
        if (existing) {
          existing.category = '🔥 Top Gainers';
        }
      });
    }

    // Add top losers with special badge
    if (marketData.topLosers && Array.isArray(marketData.topLosers)) {
      marketData.topLosers.forEach(loser => {
        const existing = assets.find(a => a.ticker === loser.ticker);
        if (existing) {
          existing.category = '📉 Top Losers';
        }
      });
    }

    // Add most active with special badge
    if (marketData.mostActive && Array.isArray(marketData.mostActive)) {
      marketData.mostActive.forEach(active => {
        const existing = assets.find(a => a.ticker === active.ticker);
        if (existing) {
          existing.category = '⚡ Most Active';
        }
      });
    }

    console.log(`✅ TOTAL ASSETS INDEXED: ${assets.length}`);
    
    if (assets.length === 0) {
      console.warn('⚠️ NO ASSETS FOUND! Falling back to trending tickers.');
      setAllAssets(TRENDING_TICKERS);
    } else {
      setAllAssets(assets);
    }
  }, [marketData]);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter results
  useEffect(() => {
    if (!query.trim()) {
      // Show trending when no query
      const trending = allAssets
        .filter(a => a.category.includes('🔥') || a.category.includes('⭐') || a.category.includes('⚡'))
        .slice(0, 10);
      
      if (trending.length === 0) {
        setFilteredResults(allAssets.slice(0, 10));
      } else {
        setFilteredResults(trending);
      }
      return;
    }

    // Fuzzy search
    const lowerQuery = query.toLowerCase();
    const results = allAssets.filter(asset => {
      const tickerMatch = asset.ticker.toLowerCase().includes(lowerQuery);
      const nameMatch = asset.name.toLowerCase().includes(lowerQuery);
      return tickerMatch || nameMatch;
    });

    // Sort: exact matches first
    results.sort((a, b) => {
      const aExact = a.ticker.toLowerCase() === lowerQuery;
      const bExact = b.ticker.toLowerCase() === lowerQuery;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return a.ticker.localeCompare(b.ticker);
    });

    console.log(`🔍 Search "${query}": Found ${results.length} results`);
    setFilteredResults(results.slice(0, 8));
    setSelectedIndex(0);
  }, [query, allAssets]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < filteredResults.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredResults[selectedIndex]) {
          handleSelect(filteredResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Select asset
  const handleSelect = (asset) => {
    console.log('🚀 Navigating to:', `/asset/${asset.type}/${asset.ticker}`);
    navigate(`/asset/${asset.type}/${asset.ticker}`);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
      {/* Search Input */}
      <div
        style={{
          position: 'relative',
          background: theme.cardBg,
          border: `2px solid ${isOpen ? theme.borderHover : theme.border}`,
          borderRadius: '16px',
          padding: '0.75rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backdropFilter: 'blur(10px)',
          boxShadow: isOpen 
            ? '0 8px 32px rgba(212, 175, 55, 0.2)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
          cursor: 'text',
          transform: isOpen ? 'scale(1.02)' : 'scale(1)'
        }}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {/* Search Icon */}
        <div style={{
          fontSize: '1.25rem',
          color: isOpen ? '#D4AF37' : theme.textSecondary,
          transition: 'all 0.3s',
          animation: isOpen ? 'pulse 2s infinite' : 'none'
        }}>
          🔍
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar ativo... (ex: PETR4, Vale, Bitcoin)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: theme.textPrimary,
            fontSize: '1rem',
            fontWeight: '500'
          }}
        />

        {/* Clear Button */}
        {query && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setQuery('');
              inputRef.current?.focus();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: '1.25rem',
              padding: '0.25rem',
              transition: 'all 0.3s'
            }}
            onMouseEnter={e => e.target.style.color = '#ef4444'}
            onMouseLeave={e => e.target.style.color = theme.textSecondary}
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            left: 0,
            right: 0,
            background: theme.cardBg,
            border: `2px solid ${theme.borderHover}`,
            borderRadius: '16px',
            maxHeight: '400px',
            overflowY: 'auto',
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
            zIndex: 1000,
            animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            background: theme.cardBg,
            backdropFilter: 'blur(10px)',
            zIndex: 1
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: 'bold',
              color: theme.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {query ? `${filteredResults.length} resultado(s)` : `🔥 Trending (${allAssets.length} ativos disponíveis)`}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: theme.textSecondary,
              opacity: 0.7
            }}>
              ↑↓ navegar • Enter selecionar
            </div>
          </div>

          {/* Results */}
          {filteredResults.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: theme.textSecondary
            }}>
              Nenhum resultado encontrado para "{query}"
            </div>
          ) : (
            <div style={{ padding: '0.5rem' }}>
              {filteredResults.map((result, index) => (
                <SearchResultItem
                  key={`${result.ticker}-${index}`}
                  result={result}
                  isSelected={index === selectedIndex}
                  onClick={() => handleSelect(result)}
                  theme={theme}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};

// Search Result Item
const SearchResultItem = ({ result, isSelected, onClick, theme }) => {
  const isPositive = result.change >= 0;
  
  return (
    <div
      onClick={onClick}
      style={{
        padding: '0.75rem 1rem',
        margin: '0.25rem 0',
        borderRadius: '12px',
        cursor: 'pointer',
        background: isSelected ? theme.itemBgHover : 'transparent',
        border: `2px solid ${isSelected ? theme.borderHover : 'transparent'}`,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transform: isSelected ? 'translateX(4px)' : 'translateX(0)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = theme.itemBgHover;
        e.currentTarget.style.borderColor = theme.borderHover;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.transform = 'translateX(0)';
        }
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span style={{
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#D4AF37',
            fontFamily: 'monospace'
          }}>
            {result.ticker}
          </span>
          <span style={{
            fontSize: '0.7rem',
            color: theme.textSecondary,
            opacity: 0.8
          }}>
            {result.category}
          </span>
        </div>
        <div style={{
          fontSize: '0.85rem',
          color: theme.textSecondary
        }}>
          {result.name}
        </div>
      </div>

      {/* Price & Change */}
      {result.price !== undefined && result.price > 0 && (
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: 'bold',
            color: theme.textPrimary,
            fontFamily: 'monospace'
          }}>
            R$ {result.price.toFixed(2)}
          </div>
          {result.change !== undefined && result.change !== 0 && (
            <div style={{
              fontSize: '0.85rem',
              fontWeight: 'bold',
              color: isPositive ? '#10b981' : '#ef4444'
            }}>
              {isPositive ? '▲' : '▼'} {Math.abs(result.change).toFixed(2)}%
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModernSearchBar;